import { useCallback, useEffect, useMemo, useState } from "react";
import { reportsService } from "@/services";
import type { OutstandingReport } from "@/services/types";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatINR } from "@/utils/format";
import { AGEING_ORDER, AGEING_META } from "../components/labels";
import { exportReportCSV, csvAmount } from "@/utils/report";

type ViewMode = "grouped" | "flat";

export default function OutstandingReportPage() {
  const [data, setData] = useState<OutstandingReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>("grouped");

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await reportsService.getOutstandingReport());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the outstanding report.");
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const contactCount = useMemo(() => data?.rows.filter((r) => r.totalOutstanding > 0).length ?? 0, [data]);

  const exportContacts = () => {
    if (!data) return;
    exportReportCSV({
      filename: "defaulter-contacts",
      reportName: "Defaulter Contact List",
      filters: [`Generated ${formatDate(new Date().toISOString())}`],
      columns: [
        { header: "Name", value: (r) => r.name },
        { header: "Phone", value: (r) => r.phone },
        { header: "Total outstanding", value: (r) => csvAmount(r.outstanding) },
      ],
      rows: data.rows.map((r) => ({ name: r.memberName, phone: r.memberPhone, outstanding: r.totalOutstanding })),
    });
  };

  const exportReport = () => {
    if (!data) return;
    exportReportCSV({
      filename: "outstanding-report",
      reportName: "Outstanding & Defaulter Report",
      filters: [`View: ${view === "grouped" ? "grouped by member" : "demand-level"}`],
      columns: [
        { header: "Member", value: (r) => r.member },
        { header: "Phone", value: (r) => r.phone },
        { header: "Group", value: (r) => r.group },
        { header: "Cycle", value: (r) => r.cycle },
        { header: "Due date", value: (r) => r.due },
        { header: "Days overdue", value: (r) => r.days },
        { header: "Balance", value: (r) => csvAmount(r.balance) },
        { header: "Ageing", value: (r) => r.bucket },
      ],
      rows: data.flat.map((d) => ({
        member: d.memberName,
        phone: d.memberPhone,
        group: d.groupName,
        cycle: d.cycle,
        due: formatDate(d.dueDate),
        days: d.daysOverdue,
        balance: d.balance,
        bucket: AGEING_META[bucketOf(d.daysOverdue)].label,
      })),
    });
  };

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Reports" to="/reports" />
            <BreadcrumbItem label="Outstanding & defaulters" isLast />
          </>
        }
        title="Outstanding & defaulters"
        subtitle="Every unpaid demand aged into buckets — older money is progressively less likely to be recovered."
        actions={
          <>
            <Button variant="outline" size="sm" icon={<i className="ri-contacts-book-line" />} onClick={exportContacts}>
              Contact list ({contactCount})
            </Button>
            <Button variant="outline" size="sm" icon={<i className="ri-download-2-line" />} onClick={exportReport}>
              Export
            </Button>
          </>
        }
      />

      {error ? <div className="mb-3"><ErrorState message={error} onRetry={load} /></div> : null}

      {!data ? (
        <div className="h-64 animate-pulse rounded-lg bg-background-200" />
      ) : (
        <>
          {/* Ageing summary cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {AGEING_ORDER.map((b) => (
              <div key={b} className="rounded-lg border border-background-200 bg-background-50 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-foreground-500">{AGEING_META[b].label}</p>
                  <Badge color={AGEING_META[b].badge}>{data.bucketTotals[b] > 0 ? "Due" : "Clear"}</Badge>
                </div>
                <p className={`tnum mt-2 text-xl font-semibold ${AGEING_META[b].tone === "danger" ? "text-danger-600" : AGEING_META[b].tone === "warning" ? "text-warning-600" : "text-foreground-950"}`}>
                  {formatINR(data.bucketTotals[b] ?? 0)}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              <button type="button" onClick={() => setView("grouped")} className={`cursor-pointer rounded-full px-3 py-1.5 text-sm font-medium ${view === "grouped" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-100"}`}>
                Grouped by member ({data.rows.length})
              </button>
              <button type="button" onClick={() => setView("flat")} className={`cursor-pointer rounded-full px-3 py-1.5 text-sm font-medium ${view === "flat" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-100"}`}>
                Demand-level ({data.flat.length})
              </button>
            </div>
            <p className="text-sm text-foreground-600">
              Total outstanding: <span className="tnum font-semibold text-foreground-900">{formatINR(data.grandTotal)}</span>
            </p>
          </div>

          <Card className="mt-3">
            {data.rows.length === 0 ? (
              <EmptyState icon="ri-check-double-line" title="No outstanding dues" description="Every unpaid demand has been cleared." />
            ) : view === "grouped" ? (
              <div className="overflow-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-background-100/80 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                      <th className="px-3 py-2 text-left">Member</th>
                      <th className="px-3 py-2 text-left">Phone</th>
                      <th className="px-3 py-2 text-left">Demands</th>
                      <th className="px-3 py-2 text-right">Total outstanding</th>
                      <th className="px-3 py-2 text-left">Ageing breakdown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <MemberBlock key={r.memberId} report={data} memberId={r.memberId} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overflow-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-background-100/80 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                      <th className="px-3 py-2 text-left">Member</th>
                      <th className="px-3 py-2 text-left">Phone</th>
                      <th className="px-3 py-2 text-left">Group</th>
                      <th className="px-3 py-2 text-right">Cycle</th>
                      <th className="px-3 py-2 text-left">Due date</th>
                      <th className="px-3 py-2 text-right">Days</th>
                      <th className="px-3 py-2 text-right">Balance</th>
                      <th className="px-3 py-2 text-left">Ageing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.flat.map((d) => (
                      <FlatRow key={d.id} d={d} />
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-background-200 bg-background-100/60 font-semibold">
                      <td className="px-3 py-2 text-foreground-500" colSpan={6}>Total outstanding</td>
                      <td className="px-3 py-2 text-right"><CurrencyDisplay amount={data.grandTotal} hideSymbol /></td>
                      <td className="px-3 py-2" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function bucketOf(days: number): (typeof AGEING_ORDER)[number] {
  if (days <= 0) return "not_due";
  if (days <= 30) return "b1_30";
  if (days <= 60) return "b31_60";
  if (days <= 90) return "b61_90";
  return "b90_plus";
}

function FlatRow({ d }: { d: OutstandingReport["flat"][number] }) {
  const b = bucketOf(d.daysOverdue);
  return (
    <tr className="border-t border-background-100 hover:bg-background-100/50">
      <td className="px-3 py-2 font-medium text-foreground-900">{d.memberName}</td>
      <td className="tnum px-3 py-2 text-foreground-600">{d.memberPhone}</td>
      <td className="px-3 py-2 text-foreground-700">{d.groupName} <span className="tnum text-xs text-foreground-400">{d.groupCode}</span></td>
      <td className="tnum px-3 py-2 text-right">{d.cycle}</td>
      <td className="tnum whitespace-nowrap px-3 py-2 text-foreground-600">{formatDate(d.dueDate)}</td>
      <td className="tnum px-3 py-2 text-right text-foreground-700">{d.daysOverdue}</td>
      <td className="px-3 py-2 text-right"><CurrencyDisplay amount={d.balance} hideSymbol tone="danger" /></td>
      <td className="px-3 py-2"><Badge color={AGEING_META[b].badge}>{AGEING_META[b].short}</Badge></td>
    </tr>
  );
}

function MemberBlock({ report, memberId }: { report: OutstandingReport; memberId: string }) {
  const row = report.rows.find((r) => r.memberId === memberId)!;
  return (
    <>
      <tr className="border-t border-background-200 bg-background-100/40">
        <td className="px-3 py-2 font-semibold text-foreground-900">{row.memberName}</td>
        <td className="tnum px-3 py-2 text-foreground-600">{row.memberPhone}</td>
        <td className="tnum px-3 py-2 text-foreground-600">{row.demands.length}</td>
        <td className="px-3 py-2 text-right"><CurrencyDisplay amount={row.totalOutstanding} hideSymbol tone="danger" className="font-semibold" /></td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap gap-1">
            {AGEING_ORDER.filter((b) => row.buckets[b] > 0).map((b) => (
              <span key={b} className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                <span className="h-1.5 w-1.5 rounded-full bg-secondary-400" />
                {AGEING_META[b].short}: <span className="tnum">{formatINR(row.buckets[b])}</span>
              </span>
            ))}
          </div>
        </td>
      </tr>
      {row.demands.map((d) => (
        <tr key={d.id} className="border-t border-background-100 bg-background-50">
          <td className="px-3 py-1.5 pl-6 text-xs text-foreground-400">{d.groupName}</td>
          <td className="px-3 py-1.5 text-xs text-foreground-400" />
          <td className="px-3 py-1.5 text-xs text-foreground-500">C{d.cycle} · {d.periodLabel}</td>
          <td className="px-3 py-1.5 text-right text-xs"><CurrencyDisplay amount={d.balance} hideSymbol tone="danger" /></td>
          <td className="px-3 py-1.5 text-xs text-foreground-500">Due {formatDate(d.dueDate)} · {d.daysOverdue}d overdue</td>
        </tr>
      ))}
    </>
  );
}